import React, { useEffect, useMemo } from "react";
import {Accordion, AccordionDetails, AccordionSummary, Grid, Typography, useTheme} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {FormProvider, useForm, useFormContext} from "react-hook-form";
import {FormTextField} from "../../common/form-fields-wrappers";
import {useRecoilValue} from "recoil";
import {raDetailState} from "../state";
import { useMutation } from "@tanstack/react-query";
import { fetchRaCheckInExtra } from "../../create-ra/services/http.services";
import {formatCurrency} from "../../utils";
import { getLocaleByLang } from "../../utils";
import { LOCALES } from "../../config";

const QuotationInfo = () => {
    const {t, i18n} = useTranslation(['network', 'translation']);
    const theme = useTheme();
    const raDetail = useRecoilValue(raDetailState);

    const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
        return getLocaleByLang(i18n.language);
    }, [i18n.language]);

    // check amount values a and b
    const getValidAmount = (a?: number | null, b?: number | null) => {
        if (a !== null && a !== undefined && a !== 0) return a;
        if (b !== null && b !== undefined && b !== 0) return b;
        return 0;
    };

    const checkInExtraForm = useForm({
        defaultValues: {
            amountResponseExtraDays_extraAncillaryAmount: "" as null | string,
            amountResponseExtraDays_extraDays: "" as null | string,
            amountResponseExtraDays_amountTaxIncluded: "" as null | string,
            amountResponseExtraKm_amountTaxIncluded: "" as null | string,
            amountResponseExtraKm_extraKms: "" as null | string,
            amountResponseExtraRefill_extraTermicAmount: "" as null | string,
            amountResponseExtraRefill_extraSocAmount: "" as null | string,
            amountResponseExtraRefill_refuelServiceAmount: "" as null | string,
            extraFormDamageResponse_totalDamageAmount: "" as null | string,
            extraOneWayResponse_extraAmountTaxIncluded: "" as null | string,
            extraMissingEquipmentResponse_formAmount: "" as null | string,
        }
    });

    const checkInExtraMutation = useMutation(
        fetchRaCheckInExtra,
        {
            onSuccess(data) {
                checkInExtraForm.setValue('amountResponseExtraDays_extraAncillaryAmount', formatCurrency(locale as LOCALES, data.amountResponseExtraDays.extraAncillaryAmount || 0));
                checkInExtraForm.setValue('amountResponseExtraDays_extraDays', data.amountResponseExtraDays.extraDays || 0);
                checkInExtraForm.setValue('amountResponseExtraDays_amountTaxIncluded', formatCurrency(locale as LOCALES, data.amountResponseExtraDays.amountTaxIncluded || 0));
                checkInExtraForm.setValue('amountResponseExtraKm_amountTaxIncluded', formatCurrency(locale as LOCALES, data.amountResponseExtraKm.amountTaxIncluded || 0));
                checkInExtraForm.setValue('amountResponseExtraKm_extraKms', data.amountResponseExtraKm.extraKms || 0);
                checkInExtraForm.setValue('amountResponseExtraRefill_extraTermicAmount', formatCurrency(locale as LOCALES, data.amountResponseExtraRefill.extraTermicAmount || 0));
                checkInExtraForm.setValue('amountResponseExtraRefill_extraSocAmount', formatCurrency(locale as LOCALES, data.amountResponseExtraRefill.extraSocAmount || 0));
                checkInExtraForm.setValue('amountResponseExtraRefill_refuelServiceAmount', formatCurrency(locale as LOCALES, data.amountResponseExtraRefill.refuelServiceAmount || 0));
                checkInExtraForm.setValue('extraFormDamageResponse_totalDamageAmount', formatCurrency(locale as LOCALES, data.extraFormDamageResponse.totalDamageAmount || 0));
                const extraOneWayResponse = getValidAmount(
                                            data.extraOneWayResponse?.extraAmountTaxIncluded,
                                            data.extraOneWayResponse?.deltaAmountTaxIncluded
                                        );  
                checkInExtraForm.setValue('extraOneWayResponse_extraAmountTaxIncluded', formatCurrency(locale as LOCALES, extraOneWayResponse || 0));
                checkInExtraForm.setValue('extraMissingEquipmentResponse_formAmount', formatCurrency(locale as LOCALES, data.extraMissingEquipmentResponse.formAmount || 0));
            },
            onError(error) {
                console.log(error)
            }
        }
    );

    useEffect(() => {
        if (!!raDetail) {
            checkInExtraMutation.mutate({ idRentalAgreement: raDetail.idRentalAgreement })
        }
    }, [raDetail]);

    return (
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant={"bodyLarge"}>
                    {t("raDetail.extraInfo.accordion.title")}
                </Typography>
            </AccordionSummary>

            <AccordionDetails>
                <FormProvider {...checkInExtraForm}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField
                                name={"amountResponseExtraDays_extraAncillaryAmount"}
                                label={t("raDetail.extraInfo.form.label.amountResponseExtraDays_extraAncillaryAmount")}
                                color={'primary'}
                                disabled
                                sx={{color: theme.palette.primary.main}}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField
                                name={"amountResponseExtraDays_extraDays"}
                                label={t("raDetail.extraInfo.form.label.amountResponseExtraDays_extraDays")}
                                color={'primary'}
                                disabled
                                sx={{color: theme.palette.primary.main}}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField
                                name={"amountResponseExtraDays_amountTaxIncluded"}
                                label={t("raDetail.extraInfo.form.label.amountResponseExtraDays_amountTaxIncluded")}
                                color={'primary'}
                                disabled
                                sx={{color: theme.palette.primary.main}}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField
                                name={"amountResponseExtraKm_amountTaxIncluded"}
                                label={t("raDetail.extraInfo.form.label.amountResponseExtraKm_amountTaxIncluded")}
                                color={'primary'}
                                disabled
                                sx={{color: theme.palette.primary.main}}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField
                                name={"amountResponseExtraKm_extraKms"}
                                label={t("raDetail.extraInfo.form.label.amountResponseExtraKm_extraKms")}
                                color={'primary'}
                                disabled
                                sx={{color: theme.palette.primary.main}}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField
                                name={"amountResponseExtraRefill_extraTermicAmount"}
                                label={t("raDetail.extraInfo.form.label.amountResponseExtraRefill_extraTermicAmount")}
                                color={'primary'}
                                disabled
                                sx={{color: theme.palette.primary.main}}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField
                                name={"amountResponseExtraRefill_extraSocAmount"}
                                label={t("raDetail.extraInfo.form.label.amountResponseExtraRefill_extraSocAmount")}
                                color={'primary'}
                                disabled
                                sx={{color: theme.palette.primary.main}}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField
                                name={"amountResponseExtraRefill_refuelServiceAmount"}
                                label={t("raDetail.extraInfo.form.label.amountResponseExtraRefill_refuelServiceAmount")}
                                color={'primary'}
                                disabled
                                sx={{color: theme.palette.primary.main}}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField
                                name={"extraFormDamageResponse_totalDamageAmount"}
                                label={t("raDetail.extraInfo.form.label.extraFormDamageResponse_totalDamageAmount")}
                                color={'primary'}
                                disabled
                                sx={{color: theme.palette.primary.main}}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField
                                name={"extraOneWayResponse_extraAmountTaxIncluded"}
                                label={t("raDetail.extraInfo.form.label.extraOneWayResponse_extraAmountTaxIncluded")}
                                color={'primary'}
                                disabled
                                sx={{color: theme.palette.primary.main}}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField
                                name={"extraMissingEquipmentResponse_formAmount"}
                                label={t("raDetail.extraInfo.form.label.extraMissingEquipmentResponse_formAmount")}
                                color={'primary'}
                                disabled
                                sx={{color: theme.palette.primary.main}}
                            />
                        </Grid>
                    </Grid>
                </FormProvider>
            </AccordionDetails>
        </Accordion>
    );
}

export default QuotationInfo;